import { NoDataSmall } from "@/components/common/section-components/NoDataSmall";
import { AppColors } from "@/styles/colors";
import { Box, Table, Text } from "@chakra-ui/react";
import { AccountCVU } from "../schema/account.schema";
import { useEffect, useState } from "react";
import {
  CVUBalanceResponse,
  fetchCVUBalanceHandler,
} from "../schema/handler-functions";

interface Props {
  accountCvu?: AccountCVU | null;
}

export const AccountCVUData = ({ accountCvu }: Props) => {
  const [loadingCVUBalance, setLoadingCVUBalance] = useState(false);
  const [cvuBalance, setCvuBalance] = useState<CVUBalanceResponse | null>(null);
  const [cvuBalanceError, setCvuBalanceError] = useState(false);

  const formatSaldoArs = (saldo: string) => {
    const cleaned = saldo.replaceAll(/[^0-9,.-]/g, "").trim();
    if (!cleaned) return null;

    let normalized = cleaned;
    if (normalized.includes(",") && normalized.includes(".")) {
      normalized = normalized.replaceAll(".", "").replace(",", ".");
    } else if (normalized.includes(",")) {
      normalized = normalized.replace(",", ".");
    }

    const amount = Number(normalized);
    if (Number.isNaN(amount)) return null;

    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      currencyDisplay: "symbol",
    }).format(amount);
  };

  useEffect(() => {
    let cancelled = false;

    fetchCVUBalanceHandler({
      cvu: accountCvu?.cvu,
      isCancelled: () => cancelled,
      setLoadingCVUBalance,
      setCvuBalance,
      setCvuBalanceError,
    });

    return () => {
      cancelled = true;
    };
  }, [accountCvu?.cvu]);

  if (!accountCvu) return <NoDataSmall />;

  let saldoCvuLabel = "No disponible";
  if (!cvuBalanceError && cvuBalance?.saldo) {
    saldoCvuLabel = formatSaldoArs(cvuBalance.saldo) ?? "No disponible";
  }

  let saldoCvuDisplay = saldoCvuLabel;
  if (loadingCVUBalance) saldoCvuDisplay = "Cargando...";

  return (
    <Box marginTop="12px" borderRadius="12px">
      <Table.ScrollArea
        borderRadius="12px"
        css={{
          "@media (max-width: 1350px) and (min-width: 1272px)": {
            maxWidth: "950px",
            height: "122px",
          },
          "@media (max-width: 1414px) and (min-width: 1351px)": {
            maxWidth: "1100px",
            height: "185px",
          },
          "@media (max-width: 1590px) and (min-width: 1415px)": {
            maxWidth: "1250px",
            height: "200px",
          },
          "@media (max-width: 1696px) and (min-width: 1591px)": {
            maxWidth: "1350px",
            height: "233px",
          },
          "@media (max-width: 1900px)": {
            maxWidth: "1500px",
            height: "324.5px",
          },
          "@media (min-width: 1901px)": {
            height: "324.5px",
          },
          "&::-webkit-scrollbar": {
            width: "12px",
            borderRadius: "12px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: AppColors.SCROLL_BG,
            borderRadius: "9px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: "12px",
          },
        }}
      >
        <Table.Root
          size="sm"
          striped
          borderRadius="12px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={400}
                  fontSize="12px"
                >
                  Estado
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text
                  padding="16px"
                  fontWeight={400}
                  fontSize="12px"
                  color={AppColors.BLACK_TEXT_SECONDARY}
                >
                  {accountCvu?.estado ? "Habilitado" : "Deshabilitado"}
                </Text>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={400}
                  fontSize="12px"
                >
                  N° CVU
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text
                  padding="16px"
                  fontWeight={400}
                  fontSize="12px"
                  color={AppColors.BLACK_TEXT_SECONDARY}
                >
                  {accountCvu?.cvu ?? "-"}
                </Text>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={400}
                  fontSize="12px"
                >
                  Alias:
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text
                  padding="16px"
                  fontWeight={400}
                  fontSize="12px"
                  color={AppColors.BLACK_TEXT_SECONDARY}
                >
                  {accountCvu?.alias ?? "-"}
                </Text>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={400}
                  fontSize="12px"
                >
                  CUIT/CUIL:
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text
                  padding="16px"
                  fontWeight={400}
                  fontSize="12px"
                  color={AppColors.BLACK_TEXT_SECONDARY}
                >
                  {accountCvu?.cuit ?? "-"}
                </Text>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>
                <Text
                  padding="16px"
                  color={AppColors.GRAY_TEXT}
                  fontWeight={400}
                  fontSize="12px"
                >
                  Saldo en CVU
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text
                  padding="16px"
                  fontWeight={400}
                  fontSize="12px"
                  color={AppColors.BLACK_TEXT_SECONDARY}
                >
                  {saldoCvuDisplay}
                </Text>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    </Box>
  );
};
